import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../ui/button';
import {
  Sparkle, Briefcase, Users, ShieldCheck, ArrowsLeftRight,
  CaretLeft, CaretRight, X, CheckCircle, Lightbulb, Target,
} from '@phosphor-icons/react';

const STORAGE_KEY = 'hikari_employer_tour_seen';

// Six-step storyboard. Each step is self-contained — illustration is built
// from CSS + icons so it renders the same on every screen size.
const STEPS = [
  {
    key: 'welcome',
    eyebrow: 'TOUR · 60 SECONDS',
    title: 'Hire with confidence — in five steps.',
    body:
      "Hikari replaces resume screening with verifiable proof of skills, structured assessments, and consistency-based scoring. Here's the whole engine in one minute.",
    Illustration: () => (
      <div className="flex items-center justify-center h-44 bg-secondary/40 border border-border">
        <Sparkle size={64} weight="fill" className="text-primary" />
      </div>
    ),
  },
  {
    key: 'post',
    eyebrow: 'STEP 1 · POST A ROLE',
    title: 'Pick a role family. Add custom questions. Done.',
    body:
      'Choose from 74 spec-seeded role families across 13 sectors. Add job-specific written questions if you want — Hikari uses them to grade fit, not just fill in fields.',
    Illustration: () => (
      <div className="h-44 bg-foreground text-background p-5 border border-border">
        <div className="label-eyebrow text-accent mb-2">NEW ROLE</div>
        <div className="font-display font-black text-2xl tracking-tighter mb-3">
          Senior Operations Lead
        </div>
        <div className="flex flex-wrap gap-1">
          {['attention_to_detail', 'time_management', 'vendor_ops'].map((s) => (
            <span key={s} className="label-eyebrow bg-accent text-foreground px-2 py-0.5">
              {s}
            </span>
          ))}
        </div>
      </div>
    ),
  },
  {
    key: 'rank',
    eyebrow: 'STEP 2 · SEE RANKED CANDIDATES',
    title: 'Every applicant ranked by proof, not pedigree.',
    body:
      "We score every candidate on four layers — performance, consistency, reliability, artifacts — and surface the top fit instantly. No more 100-resume PDF stacks.",
    Illustration: () => (
      <div className="h-44 bg-white border border-border divide-y divide-border">
        {[
          { name: 'Amara N.', fit: 93 },
          { name: 'Devon R.', fit: 87 },
          { name: 'Yuki T.', fit: 81 },
        ].map((c) => (
          <div key={c.name} className="flex items-center gap-3 p-2.5">
            <span
              className={`mono font-bold w-12 h-10 flex items-center justify-center text-sm ${
                c.fit >= 90 ? 'bg-primary text-primary-foreground' :
                c.fit >= 80 ? 'bg-foreground text-background' : 'bg-accent text-foreground'
              }`}
            >
              {c.fit}
            </span>
            <span className="text-sm font-medium flex-1">{c.name}</span>
            <span className="label-eyebrow text-muted-foreground">VIEW</span>
          </div>
        ))}
      </div>
    ),
  },
  {
    key: 'why',
    eyebrow: 'STEP 3 · "WHY THIS CANDIDATE?"',
    title: 'Open any profile to see the reasoning.',
    body:
      'Plain-English explanation, radar breakdown, matched signals, gaps to probe in interview, and the proof points that earned the score. Every number is defensible.',
    Illustration: () => (
      <div className="h-44 bg-white border border-border p-3 grid grid-cols-2 gap-2">
        <div className="flex items-center justify-center bg-primary text-primary-foreground mono font-black text-3xl">
          93
        </div>
        <div className="space-y-1.5">
          {['Performance · 92', 'Consistency · 100', 'Reliability · 100', 'Artifacts · 60'].map(
            (l) => (
              <div key={l} className="flex items-center gap-2">
                <div className="h-1 flex-1 bg-secondary">
                  <div
                    className="h-1 bg-primary"
                    style={{ width: `${parseInt(l.split('·')[1], 10)}%` }}
                  />
                </div>
                <span className="mono text-[10px] text-muted-foreground w-20">{l}</span>
              </div>
            )
          )}
        </div>
      </div>
    ),
  },
  {
    key: 'pipeline',
    eyebrow: 'STEP 4 · INVITE & ADVANCE',
    title: 'Move candidates through a real pipeline.',
    body:
      'Invite → Interviewed → Offered → Hired. One click moves the candidate, sends them a branded email, and timestamps the timeline. Nothing falls through the cracks.',
    Illustration: () => (
      <div className="h-44 bg-foreground text-background p-4 grid grid-cols-5 gap-1">
        {['Applied', 'Invited', 'Interviewed', 'Offered', 'Hired'].map((s, i) => (
          <div
            key={s}
            className={`p-2 border ${
              i === 1
                ? 'bg-accent text-foreground border-accent'
                : i < 1
                ? 'border-accent/50 text-accent'
                : 'border-background/20 text-background/60'
            }`}
          >
            <div className="label-eyebrow flex items-center gap-1">
              {i < 1 && <CheckCircle size={10} weight="fill" />}
              <span className="mono">{String(i + 1).padStart(2, '0')}</span>
            </div>
            <div className="font-display font-black text-xs mt-1">{s}</div>
          </div>
        ))}
      </div>
    ),
  },
  {
    key: 'done',
    eyebrow: 'YOU\'RE READY',
    title: 'Now — post your first role.',
    body:
      "We've already seeded 74 role families, so you can spin up a job in 30 seconds. Or jump to your dashboard and explore the demo data first.",
    Illustration: () => (
      <div className="h-44 bg-secondary/40 border border-border flex flex-col items-center justify-center gap-2">
        <CheckCircle size={56} weight="fill" className="text-primary" />
        <div className="mono text-xs text-muted-foreground">TOUR COMPLETE</div>
      </div>
    ),
  },
];

const STEP_ICON = [Sparkle, Briefcase, Users, ShieldCheck, ArrowsLeftRight, CheckCircle];

export const EmployerTour = ({ open, onClose }) => {
  const [idx, setIdx] = useState(0);

  // Reset to first slide every time the tour opens
  useEffect(() => {
    if (open) setIdx(0);
  }, [open]);

  // Esc key closes
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowRight') setIdx((i) => Math.min(i + 1, STEPS.length - 1));
      else if (e.key === 'ArrowLeft') setIdx((i) => Math.max(i - 1, 0));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const finish = useCallback(() => {
    try { localStorage.setItem(STORAGE_KEY, '1'); } catch { /* noop */ }
    onClose();
  }, [onClose]);

  if (!open) return null;
  const step = STEPS[idx];
  const Illustration = step.Illustration;
  const isLast = idx === STEPS.length - 1;

  return (
    <div
      className="fixed inset-0 z-[80] bg-foreground/70 backdrop-blur-sm flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      data-testid="employer-tour"
    >
      <div className="bg-white border border-border w-full max-w-[640px] shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-border">
          <div className="flex items-center gap-2">
            <Sparkle size={18} weight="fill" className="text-primary" />
            <div className="label-eyebrow">HIKARI · EMPLOYER TOUR</div>
          </div>
          <button
            onClick={finish}
            className="text-muted-foreground hover:text-foreground"
            aria-label="Close tour"
            data-testid="tour-close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="p-6">
          <div className="label-eyebrow text-primary mb-2">{step.eyebrow}</div>
          <h2
            className="font-display font-black text-2xl lg:text-3xl tracking-tighter leading-tight mb-3"
            data-testid="tour-step-title"
          >
            {step.title}
          </h2>
          <p className="text-sm text-muted-foreground leading-relaxed mb-5">{step.body}</p>
          <Illustration />
        </div>

        {/* Progress dots */}
        <div className="px-6 pb-3 flex items-center gap-1.5" data-testid="tour-progress">
          {STEPS.map((s, i) => {
            const Ic = STEP_ICON[i] || Lightbulb;
            return (
              <button
                key={s.key}
                onClick={() => setIdx(i)}
                className={`flex-1 h-1.5 transition-colors ${
                  i === idx ? 'bg-primary' : i < idx ? 'bg-foreground' : 'bg-secondary'
                }`}
                aria-label={`Go to step ${i + 1}`}
                data-testid={`tour-dot-${i}`}
              >
                <span className="sr-only"><Ic size={10} /></span>
              </button>
            );
          })}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 p-5 border-t border-border bg-secondary/30">
          <button
            onClick={finish}
            className="label-eyebrow text-muted-foreground hover:text-foreground"
            data-testid="tour-skip"
          >
            Skip tour
          </button>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              className="rounded-none"
              onClick={() => setIdx((i) => Math.max(i - 1, 0))}
              disabled={idx === 0}
              data-testid="tour-prev"
            >
              <CaretLeft size={14} className="mr-1" /> Back
            </Button>
            {!isLast ? (
              <Button
                size="sm"
                className="rounded-none"
                onClick={() => setIdx((i) => Math.min(i + 1, STEPS.length - 1))}
                data-testid="tour-next"
              >
                Next <CaretRight size={14} className="ml-1" />
              </Button>
            ) : (
              <>
                <Link to="/employer/jobs">
                  <Button size="sm" className="rounded-none" onClick={finish} data-testid="tour-cta-jobs">
                    <Target size={14} className="mr-1" /> Post your first role
                  </Button>
                </Link>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

/** Hook used by the dashboard to auto-open + provide a manual trigger. */
export const useEmployerTour = () => {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    try {
      const seen = localStorage.getItem(STORAGE_KEY);
      if (!seen) {
        // Defer until after first paint so the dashboard appears behind the tour
        const t = setTimeout(() => setOpen(true), 600);
        return () => clearTimeout(t);
      }
    } catch { /* localStorage blocked — silently skip */ }
    return undefined;
  }, []);
  return {
    open,
    openTour: () => setOpen(true),
    closeTour: () => setOpen(false),
  };
};

export default EmployerTour;
